import axios from 'axios';
import { anonymizeOffer } from './anonymization';

const OPENAI_API_KEY = import.meta.env.VITE_OPENAI_API_KEY;
const OPENAI_API_URL = 'https://api.openai.com/v1/chat/completions';

if (!OPENAI_API_KEY) {
  console.warn("⚠️ La clé API OpenAI est manquante. Vérifie ton fichier .env !");
}

interface OpenAIRequest {
  messages: Array<{
    role: 'system' | 'user';
    content: string;
  }>;
  model: string;
  temperature: number;
  top_p: number;
  stream: boolean;
}

export const generateJobPost = async (
  companyName: string,
  position: string,
  location: string,
  experience: string,
  skills: string,
  contractType: string,
  tone: string,
  jobLink?: string,
  companyValues?: string[],
  companyWebsite?: string,
  originalText?: string,
  benefits?: string[],
  isBlinded?: boolean,
  sensitiveInfo?: Record<string, any>
): Promise<string> => {
  if (!OPENAI_API_KEY) {
    throw new Error("La clé API OpenAI est requise pour générer des offres d'emploi.");
  }

  if (!companyName || !position) {
    throw new Error("Le nom de l'entreprise et le poste sont requis.");
  }

  const hasCompanyValues = companyValues && companyValues.length > 0;
  const hasCompanyWebsite = companyWebsite && companyWebsite.trim() !== '';

  let systemMessage = `Tu es un expert en marque employeur et en rédaction d'offres d'emploi pour LinkedIn. 
${originalText ? "Tu dois améliorer l'offre d'emploi fournie en la rendant plus attractive" : "Ta mission est de créer une offre d'emploi professionnelle et engageante"}.
${hasCompanyWebsite ? "Utilise le site web de l'entreprise pour t'inspirer de leur langage, ton et valeurs." : ''}
Utilise un ton ${tone.toLowerCase()}.

${isBlinded ? `IMPORTANT: Cette offre doit être anonymisée. Ne mentionne pas directement :
- Le nom de l'entreprise (utilise une description générique)
- Les adresses exactes
- Les noms de marques ou de produits spécifiques
- Toute autre information permettant d'identifier l'entreprise` : ''}

STRUCTURE OBLIGATOIRE :
Chaque section DOIT être séparée par DEUX sauts de ligne.
Utilise les emojis pour les titres de section exactement comme indiqué.

📋 PRÉSENTATION DU POSTE

Une accroche impactante qui présente le poste de manière attractive et donne envie de continuer la lecture.


🏢 À PROPOS DE L'ENTREPRISE

${isBlinded ? 'Description générique mais pertinente de l\'entreprise, son secteur et sa position sur le marché.' : 'Présentation claire et concise de l\'entreprise, ses valeurs et sa culture.'}
${hasCompanyWebsite ? "Intègre des éléments de langage et valeurs trouvés sur leur site web." : ''}


🎯 MISSIONS PRINCIPALES

Liste détaillée des responsabilités et objectifs du poste.


📚 PRÉREQUIS

- Formation requise
- Expérience nécessaire
- Compétences techniques
- Soft skills importants


${benefits && benefits.length > 0 ? `💎 NOS AVANTAGES

${benefits.join('\n')}


` : ''}📮 PROCESSUS DE CANDIDATURE

Instructions claires pour postuler et étapes du processus de recrutement.


#️⃣ HASHTAGS PERTINENTS
Maximum 5 hashtags pertinents pour LinkedIn`;

  let userMessage = '';

  if (originalText && originalText.trim()) {
    userMessage = `${isBlinded ? 'Anonymise et améliore' : 'Améliore'} l'offre d'emploi suivante avec un ton ${tone.toLowerCase()} :

Informations complémentaires :
${companyName ? `- Entreprise : ${companyName}` : ''}
${position ? `- Poste : ${position}` : ''}
${location ? `- Lieu : ${location}` : ''}
${experience ? `- Expérience : ${experience}` : ''}
${skills ? `- Compétences : ${skills}` : ''}
${contractType ? `- Type de contrat : ${contractType}` : ''}
${jobLink ? `- Lien : ${jobLink}` : ''}
${hasCompanyValues ? `- Valeurs : ${companyValues.join(', ')}` : ''}
${hasCompanyWebsite ? `- Site web : ${companyWebsite}` : ''}

Offre originale à améliorer :
---
${originalText}
---`;
  } else {
    userMessage = `Génère une offre d'emploi ${isBlinded ? 'anonymisée' : ''} avec les informations suivantes :

Informations essentielles :
- Entreprise : ${companyName}
- Poste : ${position}
- Lieu : ${location}
- Expérience : ${experience}
- Compétences : ${skills}
- Type de contrat : ${contractType}
${jobLink ? `- Lien pour postuler : ${jobLink}` : ''}
${hasCompanyValues ? `- Valeurs de l'entreprise : ${companyValues.join(', ')}` : ''}
${hasCompanyWebsite ? `- Site web : ${companyWebsite} (utilise le contenu du site pour personnaliser l'offre)` : ''}

Instructions spécifiques :
- Ton ${tone.toLowerCase()}
- Structure claire avec DEUX sauts de ligne entre les sections
- Mise en valeur des points clés
- Appel à l'action clair${jobLink ? ' avec le lien fourni' : ''}
${hasCompanyWebsite ? "- Intègre le style et les valeurs de l'entreprise trouvés sur leur site web" : ''}`;
  }

  const request: OpenAIRequest = {
    messages: [
      { role: 'system', content: systemMessage },
      { role: 'user', content: userMessage }
    ],
    model: "gpt-3.5-turbo",
    temperature: 0.7,
    top_p: 0.9,
    stream: false
  };

  try {
    console.log('🚀 Envoi de la requête à l\'API OpenAI...');
    
    const response = await axios.post(OPENAI_API_URL, request, {
      headers: {
        'Authorization': `Bearer ${OPENAI_API_KEY}`,
        'Content-Type': 'application/json'
      },
      timeout: 30000
    });

    console.log('✅ Réponse reçue de l\'API');

    if (!response.data) {
      throw new Error('Réponse vide de l\'API OpenAI');
    }

    if (response.data.error) {
      console.error('❌ Erreur API:', response.data.error);
      throw new Error(response.data.error.message || 'Erreur lors de la génération de l\'offre');
    }

    if (!response.data.choices || !response.data.choices[0]?.message?.content) {
      throw new Error('Format de réponse invalide de l\'API OpenAI');
    }

    let generatedContent = response.data.choices[0].message.content.trim();
    
    if (!generatedContent) {
      throw new Error('Le contenu généré est vide');
    }

    // Ensure double line breaks between sections
    generatedContent = generatedContent
      .replace(/\n{3,}/g, '\n\n') // First normalize any excessive line breaks
      .replace(/(\n📋|\n🏢|\n🎯|\n📚|\n💎|\n📮|\n#️⃣)/g, '\n\n$1') // Then ensure double breaks before each section
      .trim();

    // Si l'offre doit être anonymisée mais que l'IA n'a pas bien fait le travail,
    // on applique notre propre anonymisation
    if (isBlinded) {
      generatedContent = anonymizeOffer(generatedContent, companyName, {
        companyDescription: `une entreprise leader dans ${sensitiveInfo?.sector || 'son secteur'}`,
        replacements: sensitiveInfo?.replacements
      });
    }

    return generatedContent;

  } catch (error: any) {
    console.error('❌ Erreur lors de la génération:', error);

    if (axios.isAxiosError(error)) {
      if (error.code === 'ECONNABORTED') {
        throw new Error('Le délai de réponse a été dépassé. Veuillez réessayer.');
      }
      if (error.response?.status === 401) {
        throw new Error('Erreur d\'authentification avec l\'API OpenAI. Vérifiez votre clé API.');
      }
      if (error.response?.status === 429) {
        throw new Error('Trop de requêtes. Veuillez attendre quelques instants avant de réessayer.');
      }
      if (error.response?.status === 400) {
        throw new Error('Requête invalide. Vérifiez les paramètres fournis.');
      }
      throw new Error(
        error.response?.data?.error?.message || 
        'Erreur lors de la communication avec l\'API OpenAI.'
      );
    }

    throw new Error('Une erreur inattendue est survenue lors de la génération de l\'offre.');
  }
};